import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

interface ButtonProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'outline' | 'text' | 'destructive' | 'accent';
  size?: 'sm' | 'md' | 'lg';
  href?: string;
  className?: string;
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  fullWidth?: boolean;
  ariaLabel?: string;
}

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  href,
  className = '',
  onClick,
  disabled = false,
  type = 'button',
  fullWidth = false,
  ariaLabel,
}) => {
  const baseStyles = "inline-flex items-center justify-center font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring dark:focus:ring-dark-ring dark:focus:ring-offset-dark-background transition-colors duration-200";
  
  // Size classes
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg',
  };
  
  // Variant classes
  const variantClasses = {
    primary: 
      'bg-primary text-primary-foreground hover:bg-primary/90 dark:bg-dark-primary dark:text-dark-primary-foreground dark:hover:bg-dark-primary/90',
    secondary: 
      'bg-secondary text-secondary-foreground hover:bg-secondary/80 dark:bg-dark-secondary dark:text-dark-secondary-foreground dark:hover:bg-dark-secondary/80',
    outline: 
      'border border-border text-foreground hover:bg-secondary hover:text-secondary-foreground dark:border-dark-border dark:bg-dark-background dark:text-dark-foreground dark:hover:bg-dark-secondary',
    text: 
      'text-primary hover:text-primary/80 dark:text-dark-accent dark:hover:text-dark-accent/80 underline-offset-4 hover:underline',
    destructive: 
      'bg-destructive text-destructive-foreground hover:bg-destructive/90 dark:bg-dark-destructive dark:text-dark-destructive-foreground dark:hover:bg-dark-destructive/90',
    accent: 
      'bg-accent text-accent-foreground hover:bg-accent/90 dark:bg-dark-accent dark:text-dark-accent-foreground dark:hover:bg-dark-accent/90',
  };
  
  // Combine classes
  const buttonClasses = `
    ${baseStyles}
    ${sizeClasses[size]}
    ${variantClasses[variant]}
    ${fullWidth ? 'w-full' : ''}
    ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
    ${className}
  `;
  
  const buttonContent = (
    <motion.span
      className="flex items-center justify-center"
      whileHover={{ scale: disabled ? 1 : 1.02 }}
      whileTap={{ scale: disabled ? 1 : 0.98 }}
    >
      {children}
    </motion.span>
  );
  
  // Render as link if href is provided
  if (href) {
    return (
      <Link
        to={href}
        className={buttonClasses}
        aria-label={ariaLabel}
        onClick={(e) => { 
            if (disabled) { 
                e.preventDefault(); 
            } else if (onClick) { 
                onClick(); 
            } 
        }}
        aria-disabled={disabled}
      >
        {buttonContent}
      </Link>
    );
  }
  
  // Otherwise render as button
  return (
    <button
      type={type}
      className={buttonClasses}
      onClick={!disabled ? onClick : undefined}
      disabled={disabled}
      aria-label={ariaLabel}
    >
      {buttonContent}
    </button>
  );
};

export default Button;
